import type { ViewRecordFieldChange } from '@salescore/client-redux'
import type { ViewQueryNode, ViewQueryRecordNode, With } from '@salescore/core'
import { Result } from 'effect'

import type { DeepReadonly } from '../../types/DeepReadonly'
import { findIndexToInsertAt } from '../../util/computeInsertPosition'
import { generateNewChildRecord } from '../../util/generateNewChildRecord'
import { getChildRecord } from '../../util/getChildRecord'
import { getChildRecordsWithParent } from '../../util/getChildRecordsWithParent'
import { replaceChildRecordNode } from '../../util/replaceChildRecordNode'
import type { NewRecordChange } from './addNewRecords'

interface AddNewChildRecordRight {
  recordNode: ViewQueryRecordNode
  rootRecordNode: ViewQueryRecordNode & { id: string }
  change: ViewRecordFieldChange | undefined
}

export type AddNewChildRecordLeft =
  | {
      message:
        | 'このブロックは書き込み禁止です'
        | '参照先を新規作成することはできません'
        | 'ルートレコードに親レコードは存在しません'
        | 'ノードパスが不正です'
        | '親レコードが存在しません。先に親レコードを作成してください。'
        | 'ルートレコードが存在しません'
        | 'ノードが見つかりませんでした'
    }
  | undefined

const checkViewQueryRecordNodeHasId = (
  node: DeepReadonly<ViewQueryRecordNode>,
): node is DeepReadonly<ViewQueryRecordNode & { id: string }> => node.id !== undefined

/**
 * 新規子レコードを追加する関数（非破壊版）
 *
 * @param rootRecordNodes - ルートレコードノードの配列。この配列とその要素は変更されない
 * @param newRecordChange - 新規レコードの作成情報（行インデックス、内部行インデックス、ノード情報）
 *
 * @returns Result型の結果
 * - Success: 成功時
 *   - recordNode: 新規作成されたレコードノード
 *   - rootRecordNode: 更新されたルートレコードノード（新しいオブジェクト）
 *   - change: データベースへの変更差分（joinOnがundefinedまたはtype='sql'の場合はundefined）
 * - Failure: 失敗時のエラーメッセージ
 *   - 'ルートレコードに親レコードは存在しません': node.pathの長さが1の場合
 *   - 'ルートレコードが存在しません': ルートレコードが存在しない場合
 *   - 'ノードパスが不正です': テーブルノードが見つからない場合
 *   - その他: getChildRecordsWithParentまたはreplaceChildRecordNodeから伝播したエラー
 */
export const addNewChildRecord = (
  rootRecordNodes: DeepReadonly<ViewQueryRecordNode[]>,
  newRecordChange: DeepReadonly<NewRecordChange>,
): Result.Result<AddNewChildRecordRight, AddNewChildRecordLeft> => {
  const { rowIndex, innerRowIndex, parentSelectionInnerRowIndex, node, height } = newRecordChange

  if (node.path.length === 1) {
    return Result.fail({
      message: 'ルートレコードに親レコードは存在しません',
    })
  }

  // ルートレコードを求める
  const rootRecordNode = rootRecordNodes[rowIndex]
  if (rootRecordNode === undefined || !checkViewQueryRecordNodeHasId(rootRecordNode)) {
    return Result.fail({
      message: 'ルートレコードが存在しません',
    })
  }

  // 新規レコードを挿入するRecordNodeの配列を求める。親特定は挿入位置ではなく parentSelectionInnerRowIndex で行う。
  const result = getChildRecordsWithParent(rootRecordNode, { innerRowIndex: parentSelectionInnerRowIndex, node })
  if (Result.isFailure(result)) {
    return Result.fail(result.failure)
  }
  const { parentRecord, tableNode, recordNodes } = result.success

  const insertIndex = findIndexToInsertAt(innerRowIndex, recordNodes)

  // 新規レコードを生成
  const { newRecord } = generateNewChildRecord(node, parentRecord.id, {
    innerRowIndexStart: innerRowIndex,
    innerRowIndexEnd: innerRowIndex + height,
    height,
  })

  // 以降のレコードのインデックスを更新
  const subsequentRecords = recordNodes.slice(insertIndex).map((record) => ({
    ...record,
    meta: {
      ...record.meta,
      innerRowIndexStart: record.meta.innerRowIndexStart + height,
      innerRowIndexEnd: record.meta.innerRowIndexEnd + height,
    },
  }))

  // 新規レコードを挿入
  const newRecordNodes = [...recordNodes.slice(0, insertIndex), newRecord, ...subsequentRecords]
  const tableNodeIndex = parentRecord.children.findIndex((x) => x.nodeName === tableNode.nodeName)
  const newParentRecord: DeepReadonly<ViewQueryRecordNode> = {
    ...parentRecord,
    children: parentRecord.children.toSpliced(tableNodeIndex, 1, {
      ...tableNode,
      children: newRecordNodes,
    }),
  }
  const pathToParent = node.path.slice(0, -1)
  const updatedRootNodeResult = refreshRootNode(pathToParent, newParentRecord, rootRecordNode)

  if (Result.isFailure(updatedRootNodeResult)) {
    return Result.fail(updatedRootNodeResult.failure)
  }
  const updatedRootNode = updatedRootNodeResult.success

  if (!checkViewQueryRecordNodeHasId(updatedRootNode)) {
    return Result.fail({
      message: 'ルートレコードが存在しません',
    })
  }

  // メタ情報が更新された状態の子レコードを取り出す
  const getChildRecordResult = getChildRecord(updatedRootNode, node.path, newRecord.id)

  if (Result.isFailure(getChildRecordResult)) {
    return Result.fail(getChildRecordResult.failure)
  }

  return Result.succeed({
    recordNode: getChildRecordResult.success,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- 非破壊版に移行途中のため。全部移行できたら as は外す。
    rootRecordNode: updatedRootNode as With<ViewQueryRecordNode, 'id'>,
    change: generateNewChildRecordChange(updatedRootNode, node, newRecord, parentRecord),
  })
}

const refreshRootNode = (
  pathToParent: string[],
  newParentRecord: DeepReadonly<ViewQueryRecordNode>,
  rootRecordNode: DeepReadonly<ViewQueryRecordNode>,
): Result.Result<DeepReadonly<ViewQueryRecordNode>, undefined> =>
  pathToParent.length === 1
    ? // 2層構造: parentRecordがrootRecordNodeなので、newParentRecordをそのまま使う
      Result.succeed(newParentRecord)
    : // 3層以上の構造: replaceChildRecordNodeで親レコードを置き換える
      replaceChildRecordNode(rootRecordNode, pathToParent, newParentRecord)

function generateNewChildRecordChange(
  rootRecordNode: DeepReadonly<ViewQueryRecordNode>,
  node: DeepReadonly<ViewQueryNode>,
  recordNode: DeepReadonly<ViewQueryRecordNode>,
  parentRecord: DeepReadonly<ViewQueryRecordNode> | undefined,
): ViewRecordFieldChange | undefined {
  const joinOn = node.read.join?.joinOn
  if (joinOn === undefined || joinOn.type === 'sql') {
    return
  }
  const relationColumn = joinOn.meta.currentNodePropertyName
  if (node.write?.streamName === undefined || parentRecord === undefined) {
    return
  }

  return {
    rootNodeId: rootRecordNode.id,
    id: recordNode.id,
    streamName: node.write.streamName,
    fieldChanges: [],
    before: {},
    after: {
      [relationColumn]: parentRecord.id,
    },
  }
}
